
import {
  EventDto,
  ScheduleDto,
  PageScope,
  PageDto,
  PageType,
  RoomDto,
  FormDto,
  FormEntryDto,
  FormType,
  getSubmissionTitle,
  getFirstName,
  getLastName,
  getAbstract,
  LiveStreamType,
  PresentationTime,
  PresentationTimesType,
  isSuccess,
  SubmissionScope,
  OrderingQueryFilters,
} from 'fourwaves-shared';
import { Component, namespace, Vue } from 'nuxt-property-decorator';
import { NuxtError } from '@nuxt/types';
import { SessionPresentationsList, PresentationElement, SessionSingleHeader } from 'fourwaves-shared/components';

const EventsModule = namespace('events');

interface ScheduleFormEntryDto extends FormEntryDto {
  presentationTimes: PresentationTime[];
}

@Component({
  components: { SessionPresentationsList, SessionSingleHeader },
  validate({ params, store }) {
    if (!/^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/.test(params.id)) {
      return false;
    }
    const page = store.state.events.pages.find(({ type }: PageDto) => type === PageType.Schedule);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isParticipant'] && !store.getters['events/isOrganizer']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class SessionPage extends Vue {
  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.State pages!: PageDto[];
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.State currentTime!: string;
  @EventsModule.State sessions!: ScheduleDto[];
  @EventsModule.Getter liveSessions!: ScheduleDto[];
  @EventsModule.Getter isVpsOpen!: boolean;
  @EventsModule.Getter isLiveAppOpen!: boolean;
  @EventsModule.Mutation updateSession!: (session: ScheduleDto) => void;

  readonly LiveStreamType = LiveStreamType;
  readonly PresentationTimesType = PresentationTimesType;

  page: PageDto | null = null;
  submissionScope: SubmissionScope | null = null;
  isFirstLoadReady = false;
  room: RoomDto | null = null;
  submissionForm: FormDto | null = null;
  presentations: PresentationElement[] = [];
  pageSize = 50;
  totalCount = 0;

  get schedule(): ScheduleDto | null {
    return this.sessions.find(({ id }) => id === this.$route.params.id) || null;
  }

  get isBookmarkable(): boolean {
    return this.isParticipant || this.isOrganizer;
  }

  get isVps() {
    return !this.room ? false : this.room.liveStreamType === LiveStreamType.VirtualPosterSession;
  }

  get hasPresentationList() {
    if (this.isVps) return false;
    return this.presentations.length > 0;
  }

  async created() {
    this.$wait.start('fetch');

    if (!this.schedule)
      return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '404' } }));

    const { id, roomId } = this.schedule;
    if (roomId) this.room = await this.$api.getRoom(roomId);

    this.page = this.pages.find(({ type }) => type === PageType.Abstracts) || null;

    if (this.room?.liveStreamType !== LiveStreamType.VirtualPosterSession && this.page?.isVisible) {
      // fetch presentations
      this.submissionScope = this.page?.submissionScope || null;
      this.submissionForm = this.currentEvent.forms.find(({ type }: FormDto) => type === FormType.Submission)!;

      const paginatedPayload = await this.$api.getFormEntries<ScheduleFormEntryDto>({
        formId: this.submissionForm.id,
        sessionIds: [id],
        submissionScope: this.submissionScope,
        pageSize: this.pageSize,
        sortField: OrderingQueryFilters.SequentialId,
        sortDirection: 'ascending',
      });

      if (paginatedPayload && paginatedPayload.items) {
        this.totalCount = paginatedPayload.totalCount;
        this.presentations = paginatedPayload.items.map(item => this.buildPresentationElement(item, id));

        if (this.schedule?.presentationTimesType !== PresentationTimesType.None) {
          this.presentations = this.presentations.sort((a, b) =>
            new Date(b.time!.start) < new Date(a.time!.start) ? 1 : -1,
          );
        }
      }
    }

    if (this.$route.query?.scrollToPresentations) {
      this.$nextTick(() => this.$scrollTo('[data-scroll="presentations"]', { offset: -100 }));
    }

    this.$wait.end('fetch');
    this.isFirstLoadReady = true;
  }

  public buildPresentationElement(item: ScheduleFormEntryDto, sessionId: string): PresentationElement {
    return {
      ...item,
      id: item.id,
      sequentialId: item.sequentialId,
      thumbnail: item.thumbnail ? `${item.thumbnail}?fit=max&w=500&h=500` : undefined,
      submitterFirstName: getFirstName(item, this.submissionForm!),
      submitterLastName: getLastName(item, this.submissionForm!),
      time: this.getPresentationTime(item, sessionId),
      title: getSubmissionTitle(item, this.submissionForm!),
      abstract: getAbstract(item, this.submissionForm!),
      isUpvotedByCurrentUser: item.isUpvotedByCurrentUser,
      isBookmarkedByCurrentUser: item.isBookmarkedByCurrentUser,
      isDiscussionPermittedBySession: item.isDiscussionPermittedBySession,
      connectionCount: item.videoParticipantCount,
      upvoteCount: item.upvoteCount,
      commentCount: item.commentCount,
    };
  }

  public getPresentationTime(abstract: ScheduleFormEntryDto, id: string): PresentationTime {
    const [fallbackTime] = abstract.presentationTimes;
    return abstract.presentationTimes.find(({ sessionId }) => sessionId === id) || fallbackTime;
  }

  public async toggleBookmark() {
    if (!this.sessions || !this.schedule) return;

    if (this.schedule.isBookmarkedByCurrentUser) {
      const result = await this.$api.unbookmarkSession(this.schedule.id);
      if (isSuccess(result)) {
        this.updateSession({ ...this.schedule, isBookmarkedByCurrentUser: false });
      }
    } else {
      const result = await this.$api.bookmarkSession(this.schedule.id);
      if (isSuccess(result)) {
        this.updateSession({ ...this.schedule, isBookmarkedByCurrentUser: true });
      }
    }
  }

  public async toggleUpvote({ id, isUpvotedByCurrentUser, upvoteCount }: PresentationElement) {
    const canUserVote = this.isParticipant || this.isOrganizer;
    const index = this.presentations.findIndex(presentation => presentation.id === id);
    if (!this.presentations[index] || !canUserVote) return;

    const updateResult = isUpvotedByCurrentUser
      ? await this.$api.deleteFormEntryUpvote(id)
      : await this.$api.upvoteFormEntry(id);

    if (isSuccess(updateResult)) {
      this.presentations[index].upvoteCount = isUpvotedByCurrentUser ? upvoteCount - 1 : upvoteCount + 1;
      this.presentations[index].isUpvotedByCurrentUser = !isUpvotedByCurrentUser;
    }
  }

  public async togglePresentationBookmark({ id, isBookmarkedByCurrentUser }: PresentationElement) {
    if (!this.$auth.loggedIn) return;
    const index = this.presentations.findIndex(presentation => presentation.id === id);
    const updateResult = isBookmarkedByCurrentUser
      ? await this.$api.unbookmarkFormEntry(id)
      : await this.$api.bookmarkFormEntry(id);

    if (isSuccess(updateResult)) this.presentations[index].isBookmarkedByCurrentUser = !isBookmarkedByCurrentUser;
  }
}
